import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Activity,
  CheckCircle,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { FrameworkData, RequirementsPageData } from './types/requirements';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useOrganization } from '../../contexts/OrganizationContext';
import { apiEndpoints, ApiError } from '../../utils/api';
import toast from '../../design-system/toast';
import { ComplianceEmptyState } from '../../components/compliance/ComplianceEmptyState';
import { MultiSelectDropdown } from '../../components/common/MultiSelectDropdown';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import ChartErrorBoundary from '../../components/common/ChartErrorBoundary';

// ============================================================================
// API Response Types (V2 - Aggregated)
// ============================================================================

interface WindowInfo {
  start: string;
  end: string;
  days: number;
}

interface ScopeInfo {
  application_ids: string[] | null;
  application_count: number;
  repository_count: number;
}

interface ScanTypeBreakdown {
  adherence_pct: number;
  total: number;
  within_sla: number;
}

interface SeverityBreakdown {
  adherence_pct: number;
  total: number;
  within_sla: number;
}

interface AggregatedMetrics {
  open_findings: number;
  resolved_in_window: number;
  mttr_days: number;
  sla_adherence_pct: number;
}

interface PlatformHealthSummary {
  scheduled_scans_executed: number;
  scheduled_scans_expected: number;
  execution_rate_pct: number;
  scans_succeeded: number;
  scans_failed: number;
  success_rate_pct: number;
}

interface TrendPoint {
  date: string;
  adherence_pct: number;
}

interface ApplicationAdherence {
  application_id: string;
  application_name: string;
  sla_adherence_pct: number;
  open_findings: number;
}

interface ComplianceOverviewResponse {
  organization_id: string;
  window: WindowInfo;
  scope: ScopeInfo;
  aggregated: AggregatedMetrics;
  by_scan_type: Record<string, ScanTypeBreakdown>;
  by_severity: Record<string, SeverityBreakdown>;
  by_application: ApplicationAdherence[];
  platform_health: PlatformHealthSummary;
  trend: TrendPoint[];
  computedAt: string | null;
}

// ============================================================================
// Donut Chart Component
// ============================================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

const DonutChart: React.FC<DonutChartProps> = ({
  percentage,
  size = 80,
  strokeWidth = 8,
  color = '#16a34a'
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-ui-border-primary"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
};

// ============================================================================
// Progress Bar Component
// ============================================================================

interface ProgressBarProps {
  label: string;
  percentage: number;
  count?: string;
  color: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ label, percentage, count, color }) => {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ui-text-secondary">{label}</span>
        <span className="font-medium text-ui-text-primary">
          {percentage}%{count && <span className="text-ui-text-tertiary ml-1">({count})</span>}
        </span>
      </div>
      <div className="h-2 bg-ui-background-tertiary rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Framework Card Component
// ============================================================================

interface FrameworkCardProps {
  framework: FrameworkData;
}

const FrameworkCard: React.FC<FrameworkCardProps> = ({ framework }) => {
  const navigate = useNavigate();
  const percentage = framework.score.percentage;

  const getStatusColor = (pct: number | null) => {
    if (pct === null) return '#6b7280'; // gray
    if (pct >= 90) return '#16a34a'; // green
    if (pct >= 70) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  if (!framework.enabled) {
    return (
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 opacity-60">
        <h3 className="text-sm font-medium text-ui-text-primary">{framework.name}</h3>
        <p className="text-xs text-ui-text-tertiary mt-1">Disabled</p>
      </div>
    );
  }

  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 hover:border-brand-primary/50 transition-colors cursor-pointer" onClick={() => navigate(`/compliance/requirements/${framework.id}`)}>
      <div className="flex items-center gap-4">
        <div className="relative">
          <ChartErrorBoundary chartName="Framework Score">
            <DonutChart
              percentage={percentage ?? 0}
              color={getStatusColor(percentage)}
            />
          </ChartErrorBoundary>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-semibold text-ui-text-primary">
              {percentage}%
            </span>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-ui-text-primary">{framework.name}</h3>
          <p className="text-xs text-ui-text-secondary mt-0.5">
            {framework.score.passing}/{framework.score.total} controls passing
          </p>
          <div className="flex items-center gap-1 mt-2">
            {percentage === 100 ? (
              <CheckCircle className="w-3.5 h-3.5 text-brand-primary" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            )}
            <span className={`text-xs ${percentage === 100 ? 'text-brand-primary' : 'text-amber-500'}`}>
              {percentage === 100 ? 'Compliant' : 'Needs attention'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Application Health Bar Chart Component
// ============================================================================

interface ApplicationHealthChartProps {
  applications: ApplicationAdherence[];
  totalCount: number;
}

const ApplicationHealthChart: React.FC<ApplicationHealthChartProps> = ({ applications, totalCount }) => {
  // Take top 6 by highest adherence (already sorted by API)
  const topApps = applications.slice(0, 6);

  const getBarColor = (pct: number) => {
    if (pct >= 90) return 'bg-brand-primary';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getTextColor = (pct: number) => {
    if (pct >= 90) return 'text-brand-primary';
    if (pct >= 70) return 'text-amber-500';
    return 'text-red-500';
  };

  const getStatusIcon = (pct: number) => {
    if (pct >= 90) return <CheckCircle className="w-3.5 h-3.5 text-brand-primary" />;
    if (pct >= 70) return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  };

  if (topApps.length === 0) {
    return (
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 h-72 flex flex-col">
        <h3 className="text-sm font-medium text-ui-text-primary mb-3 flex-shrink-0">
          SLA Adherence by Repository
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-ui-text-tertiary">No repository data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 h-72 flex flex-col">
      <h3 className="text-sm font-medium text-ui-text-primary mb-3 flex-shrink-0">
        SLA Adherence by Repository
        {totalCount > 6 && (
          <span className="ml-2 text-xs text-ui-text-tertiary font-normal">
            (Top 6 of {totalCount})
          </span>
        )}
      </h3>
      <div className="space-y-2 overflow-y-auto flex-1 pr-1">
        {topApps.map(app => (
          <div key={app.application_id} className="flex items-center gap-2">
            {/* App name */}
            <div className="w-28 flex-shrink-0">
              <span className="text-xs text-ui-text-primary truncate block">{app.application_name}</span>
            </div>

            {/* Bar */}
            <div className="flex-1 h-5 bg-ui-background-tertiary rounded overflow-hidden">
              <div
                className={`h-full rounded transition-all duration-700 ease-out ${getBarColor(app.sla_adherence_pct)}`}
                style={{ width: `${app.sla_adherence_pct}%` }}
              />
            </div>

            {/* Percentage + Status */}
            <div className="w-14 flex items-center justify-end gap-1 flex-shrink-0">
              <span className={`text-xs font-semibold ${getTextColor(app.sla_adherence_pct)}`}>
                {app.sla_adherence_pct}%
              </span>
              {getStatusIcon(app.sla_adherence_pct)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Simple Line Chart Component
// ============================================================================

interface TrendChartProps {
  data: { date: string; adherencePct: number }[];
  days?: number;
}

const TrendChart: React.FC<TrendChartProps> = ({ data, days = 30 }) => {
  const width = 100;
  const height = 40;
  const padding = 2;

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-ui-text-primary">SLA Adherence Trend</h3>
          <span className="text-xs text-ui-text-tertiary">Last {days} days</span>
        </div>
        <div className="h-32 flex items-center justify-center text-ui-text-tertiary text-sm">
          No trend data available
        </div>
      </div>
    );
  }

  const minVal = Math.min(...data.map(d => d.adherencePct));
  const maxVal = Math.max(...data.map(d => d.adherencePct));
  const range = maxVal - minVal || 1;

  // Handle single data point - avoid division by zero
  const divisor = data.length > 1 ? data.length - 1 : 1;

  const points = data.map((d, i) => {
    const x = padding + (i / divisor) * (width - 2 * padding);
    const y = height - padding - ((d.adherencePct - minVal) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `${padding},${height - padding} ${points} ${width - padding},${height - padding}`;

  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-ui-text-primary">SLA Adherence Trend</h3>
        <span className="text-xs text-ui-text-tertiary">Last {days} days</span>
      </div>
      <div className="relative h-32">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={areaPoints} fill="url(#areaGradient)" />
          <polyline
            points={points}
            fill="none"
            stroke="#16a34a"
            strokeWidth="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-ui-text-tertiary px-1">
          <span>{data[0]?.date.slice(5)}</span>
          <span>{data[data.length - 1]?.date.slice(5)}</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ui-text-secondary">Min:</span>
          <span className="text-sm font-medium text-ui-text-primary">{minVal}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-ui-text-secondary">Max:</span>
          <span className="text-sm font-medium text-ui-text-primary">{maxVal}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-ui-text-secondary">Current:</span>
          <span className="text-sm font-medium text-brand-primary">{data[data.length - 1]?.adherencePct}%</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Platform Health Component
// ============================================================================

interface PlatformHealth {
  scheduledScansExecuted: number;
  scheduledScansExpected: number;
  executionRatePct: number;
  scansSucceeded: number;
  scansFailed: number;
  successRatePct: number;
}

interface PlatformHealthProps {
  health: PlatformHealth;
}

const PlatformHealthCard: React.FC<PlatformHealthProps> = ({ health }) => {
  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-ui-text-secondary" />
        <h3 className="text-sm font-medium text-ui-text-primary">Platform Health</h3>
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-ui-text-secondary">Scan Execution Rate</span>
            <span className="font-medium text-ui-text-primary">{health.executionRatePct}%</span>
          </div>
          <div className="h-2 bg-ui-background-tertiary rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all duration-500"
              style={{ width: `${health.executionRatePct}%` }}
            />
          </div>
          <p className="text-xs text-ui-text-tertiary mt-1">
            {health.scheduledScansExecuted}/{health.scheduledScansExpected} scheduled scans executed
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-ui-text-secondary">Scan Success Rate</span>
            <span className="font-medium text-ui-text-primary">{health.successRatePct}%</span>
          </div>
          <div className="h-2 bg-ui-background-tertiary rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all duration-500"
              style={{ width: `${health.successRatePct}%` }}
            />
          </div>
          <p className="text-xs text-ui-text-tertiary mt-1">
            {health.scansSucceeded} succeeded, {health.scansFailed} failed
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

// Date range options
const DATE_RANGE_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
];

const ComplianceOverviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialAppId = searchParams.get('application_id');
  const [selectedApps, setSelectedApps] = useState<string[]>(initialAppId ? [initialAppId] : []);
  const [debouncedSelectedApps, setDebouncedSelectedApps] = useState<string[]>(initialAppId ? [initialAppId] : []); // Debounced value for API calls
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [complianceData, setComplianceData] = useState<ComplianceOverviewResponse | null>(null);
  const [frameworksData, setFrameworksData] = useState<FrameworkData[]>([]);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);
  const [appOptions, setAppOptions] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const api = useAuthenticatedApi();
  const { currentOrganization, canWrite } = useOrganization();

  // Debounce application filter changes (300ms) to avoid rapid API calls
  // Only update if array contents actually changed to prevent unnecessary re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSelectedApps(prev => {
        // Compare array contents to avoid creating new reference if unchanged
        const prevSorted = [...prev].sort();
        const nextSorted = [...selectedApps].sort();
        if (prevSorted.length === nextSorted.length &&
            prevSorted.every((v, i) => v === nextSorted[i])) {
          return prev; // Return same reference if contents match
        }
        return selectedApps;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedApps]);

  // Fetch applications on mount (for dropdown filter)
  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentOrganization) return;
      try {
        const response = await api.get<{ applications: { id: string; name: string }[]; total: number }>(
          apiEndpoints.applications.list(currentOrganization.id)
        );
        setAppOptions(response.applications.map(app => ({ id: app.id, name: app.name })));
      } catch (error) {
        console.error('Failed to fetch applications:', error);
      }
    };
    fetchApplications();
  }, [api, currentOrganization]);

  // Fetch compliance data from the API
  const fetchComplianceData = useCallback(async () => {
    if (!currentOrganization) return;
    setIsLoadingCompliance(true);
    setError(null);
    try {
      // Build application_ids parameter if filtering (uses debounced value)
      const appIdsParam = debouncedSelectedApps.length > 0 ? debouncedSelectedApps.join(',') : undefined;

      // Fetch overview and requirements data in parallel
      const [overviewResponse, requirementsResponse] = await Promise.all([
        api.get<ComplianceOverviewResponse>(
          apiEndpoints.compliance.overview(currentOrganization.id, selectedDays, appIdsParam)
        ),
        api.get<RequirementsPageData>(
          apiEndpoints.compliance.requirements(currentOrganization.id, selectedDays, appIdsParam)
        ),
      ]);

      setComplianceData(overviewResponse);
      setFrameworksData(requirementsResponse.frameworks);
    } catch (err: any) {
      console.error('Failed to fetch compliance data:', err);
      setError('Failed to load compliance data. Please try again.');
      if (err?.status >= 500) {
        toast.error('Failed to load compliance data');
      }
    } finally {
      setIsLoadingCompliance(false);
    }
  }, [api, currentOrganization, selectedDays, debouncedSelectedApps]);

  // Fetch compliance data on mount and when days or apps change
  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  const handleRefresh = useCallback(async () => {
    if (!currentOrganization) return;
    setIsRefreshing(true);
    setError(null);
    try {
      await api.post(apiEndpoints.compliance.refresh(currentOrganization.id), {});
      toast.success('Compliance computation started. Data will update shortly.');
      // Don't wait for task completion - it runs in background
      // User can refresh page to see updated data once task completes
      setIsRefreshing(false);
    } catch (err) {
      console.error('Refresh failed:', err);
      // Handle rate limiting (429) with a specific message
      if (err instanceof ApiError && err.status === 429) {
        toast.error('Please wait 5 minutes between refreshes');
      } else {
        toast.error('Failed to start compliance computation');
      }
      setIsRefreshing(false);
    }
  }, [api, currentOrganization]);

  // Computed display data from API response
  const displayData = useMemo(() => {
    // If no API data, return null (will show loading/empty state)
    if (!complianceData) return null;

    // Transform API response to display format
    const byScanType = {
      semgrep: complianceData.by_scan_type.semgrep_scan || { adherence_pct: 0, total: 0, within_sla: 0 },
      secrets: complianceData.by_scan_type.secrets_scan || { adherence_pct: 0, total: 0, within_sla: 0 },
      deepAiScan: complianceData.by_scan_type.deep_ai_scan || { adherence_pct: 0, total: 0, within_sla: 0 },
    };

    const bySeverity = {
      critical: complianceData.by_severity.critical || { adherence_pct: 0, total: 0, within_sla: 0 },
      high: complianceData.by_severity.high || { adherence_pct: 0, total: 0, within_sla: 0 },
      medium: complianceData.by_severity.medium || { adherence_pct: 0, total: 0, within_sla: 0 },
      low: complianceData.by_severity.low || { adherence_pct: 0, total: 0, within_sla: 0 },
    };

    const platformHealth = {
      scheduledScansExecuted: complianceData.platform_health.scheduled_scans_executed,
      scheduledScansExpected: complianceData.platform_health.scheduled_scans_expected,
      executionRatePct: complianceData.platform_health.execution_rate_pct,
      scansSucceeded: complianceData.platform_health.scans_succeeded,
      scansFailed: complianceData.platform_health.scans_failed,
      successRatePct: complianceData.platform_health.success_rate_pct,
    };

    // Transform trend data to match component format
    const trend = complianceData.trend.map(t => ({
      date: t.date,
      adherencePct: t.adherence_pct,
    }));

    return {
      aggregated: {
        openFindings: complianceData.aggregated.open_findings,
        resolvedInWindow: complianceData.aggregated.resolved_in_window,
        mttrDays: complianceData.aggregated.mttr_days,
        slaAdherencePct: complianceData.aggregated.sla_adherence_pct,
      },
      byScanType: {
        semgrep: { adherencePct: byScanType.semgrep.adherence_pct, total: byScanType.semgrep.total, withinSla: byScanType.semgrep.within_sla },
        secrets: { adherencePct: byScanType.secrets.adherence_pct, total: byScanType.secrets.total, withinSla: byScanType.secrets.within_sla },
        deepAiScan: { adherencePct: byScanType.deepAiScan.adherence_pct, total: byScanType.deepAiScan.total, withinSla: byScanType.deepAiScan.within_sla },
      },
      bySeverity: {
        critical: { adherencePct: bySeverity.critical.adherence_pct, total: bySeverity.critical.total, withinSla: bySeverity.critical.within_sla },
        high: { adherencePct: bySeverity.high.adherence_pct, total: bySeverity.high.total, withinSla: bySeverity.high.within_sla },
        medium: { adherencePct: bySeverity.medium.adherence_pct, total: bySeverity.medium.total, withinSla: bySeverity.medium.within_sla },
        low: { adherencePct: bySeverity.low.adherence_pct, total: bySeverity.low.total, withinSla: bySeverity.low.within_sla },
      },
      byApplication: complianceData.by_application || [],
      platformHealth,
      trend,
      scope: complianceData.scope,
      window: complianceData.window,
    };
  }, [complianceData]);

  // Loading state with skeleton loaders
  if (isLoadingCompliance && !complianceData) {
    return (
      <div className="min-h-screen bg-ui-background-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
          {/* Header skeleton */}
          <div className="animate-pulse">
            <div className="h-8 bg-ui-background-secondary rounded w-64 mb-2" />
            <div className="h-4 bg-ui-background-secondary rounded w-96" />
          </div>

          {/* Framework cards skeleton */}
          <div>
            <div className="h-4 bg-ui-background-secondary rounded w-24 mb-3 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 h-24" />
              ))}
            </div>
          </div>

          {/* Main content skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 animate-pulse bg-ui-background-secondary border border-ui-border-primary rounded-xl h-72" />
            <div className="lg:col-span-2 animate-pulse bg-ui-background-secondary border border-ui-border-primary rounded-xl h-72" />
          </div>

          {/* Chart skeleton */}
          <div className="animate-pulse bg-ui-background-secondary border border-ui-border-primary rounded-xl h-48" />

          {/* Breakdown cards skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-ui-background-secondary border border-ui-border-primary rounded-xl h-48" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
              <button
                onClick={() => { setError(null); fetchComplianceData(); }}
                className="text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ui-text-primary">Compliance Overview</h1>
            <p className="text-sm text-ui-text-secondary mt-1">
              Monitor your compliance posture across frameworks and repositories
            </p>
          </div>
          {/* 2-column grid: Refresh tracks Applications, Last computed tracks Date range */}
          <div className="grid grid-cols-1 sm:grid-cols-[auto_auto] gap-2 sm:gap-x-3 sm:gap-y-2 w-full md:w-auto md:justify-self-end">
            {/* Row 1, col 1 */}
            <div className="w-full sm:w-auto [&>div>button]:w-full sm:[&>div>button]:w-auto">
              <MultiSelectDropdown
                label="Repositories"
                options={appOptions}
                selected={selectedApps}
                onChange={setSelectedApps}
              />
            </div>
            {/* Row 1, col 2 */}
            <div className="w-full sm:w-auto [&>div>button]:w-full sm:[&>div>button]:w-auto">
              <SingleSelectDropdown<number>
                label="Date range"
                options={DATE_RANGE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                value={selectedDays}
                onChange={setSelectedDays}
              />
            </div>
            {/* Row 2, col 1: refresh button (aligned with Applications' left edge) */}
            {canWrite ? (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || !currentOrganization}
                className="w-full sm:w-auto sm:justify-self-start flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary hover:bg-ui-background-tertiary text-ui-text-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                <span className="text-sm">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            ) : (
              <div aria-hidden="true" />
            )}
            {/* Row 2, col 2: last-computed (aligned with Date range's left edge) */}
            {complianceData?.computedAt && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-ui-text-tertiary self-center justify-self-start">
                <Clock size={14} />
                <span>
                  Last computed {formatDistanceToNow(new Date(complianceData.computedAt), { addSuffix: true })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Framework Cards */}
        <div>
          <h2 className="text-sm font-medium text-ui-text-secondary mb-3">Frameworks</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {frameworksData.map(framework => (
              <FrameworkCard key={framework.id} framework={framework} />
            ))}
          </div>
        </div>

        {/* Application Health + Key Metrics Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Application Health Chart - 3 columns */}
          <div className="lg:col-span-3">
            <ChartErrorBoundary chartName="Application Health">
              <ApplicationHealthChart
                applications={displayData?.byApplication || []}
                totalCount={displayData?.byApplication?.length || 0}
              />
            </ChartErrorBoundary>
          </div>

          {/* Key Metrics - 2 columns (using real API data) */}
          <div className="lg:col-span-2">
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 h-72 flex flex-col">
              <h3 className="text-sm font-medium text-ui-text-primary mb-3 flex-shrink-0">Key Metrics</h3>
              {displayData ? (
                <div className="grid grid-cols-2 gap-3 flex-1 content-center">
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-ui-text-secondary">Open Findings</p>
                    <p className="text-2xl font-semibold text-ui-text-primary mt-1">{displayData.aggregated.openFindings}</p>
                    <p className="text-xs text-ui-text-tertiary mt-0.5">Requires attention</p>
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-ui-text-secondary">Resolved</p>
                    <p className="text-2xl font-semibold text-ui-text-primary mt-1">{displayData.aggregated.resolvedInWindow}</p>
                    <p className="text-xs text-ui-text-tertiary mt-0.5">In last {selectedDays} days</p>
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-ui-text-secondary">MTTR</p>
                    <p className="text-2xl font-semibold text-ui-text-primary mt-1">{displayData.aggregated.mttrDays}d</p>
                    <p className="text-xs text-ui-text-tertiary mt-0.5">Mean time to resolve</p>
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-ui-text-secondary">SLA Adherence</p>
                    <p className="text-2xl font-semibold text-ui-text-primary mt-1">{displayData.aggregated.slaAdherencePct}%</p>
                    <p className="text-xs text-ui-text-tertiary mt-0.5">Overall adherence</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <ComplianceEmptyState
                    variant="no-data"
                    title="No metrics computed"
                    description={canWrite ? "Click Refresh to compute compliance metrics." : "No compliance metrics available."}
                    action={canWrite ? {
                      label: 'Refresh',
                      onClick: handleRefresh,
                      isLoading: isRefreshing,
                    } : undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trend Chart (using real API data or fallback) */}
        {displayData && displayData.trend.length > 0 ? (
          <ChartErrorBoundary chartName="Trend Chart">
            <TrendChart data={displayData.trend} days={selectedDays} />
          </ChartErrorBoundary>
        ) : (
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 h-48 flex items-center justify-center">
            <ComplianceEmptyState
              variant="no-data"
              title="No trend data"
              description="Trend data will appear after compliance metrics are computed."
            />
          </div>
        )}

        {/* Breakdowns Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* By Scan Type (using real API data) */}
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
            <h3 className="text-sm font-medium text-ui-text-primary mb-4">By Scan Type</h3>
            {displayData ? (
              <div className="space-y-4">
                <ProgressBar
                  label="Semgrep"
                  percentage={displayData.byScanType.semgrep.adherencePct}
                  count={`${displayData.byScanType.semgrep.withinSla}/${displayData.byScanType.semgrep.total}`}
                  color="bg-brand-primary"
                />
                <ProgressBar
                  label="Secrets"
                  percentage={displayData.byScanType.secrets.adherencePct}
                  count={`${displayData.byScanType.secrets.withinSla}/${displayData.byScanType.secrets.total}`}
                  color="bg-blue-500"
                />
                <ProgressBar
                  label="Deep AI Scan"
                  percentage={displayData.byScanType.deepAiScan.adherencePct}
                  count={`${displayData.byScanType.deepAiScan.withinSla}/${displayData.byScanType.deepAiScan.total}`}
                  color="bg-indigo-500"
                />
              </div>
            ) : (
              <p className="text-sm text-ui-text-tertiary">No data available</p>
            )}
          </div>

          {/* By Severity (using real API data) */}
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
            <h3 className="text-sm font-medium text-ui-text-primary mb-4">By Severity</h3>
            {displayData ? (
              <div className="space-y-4">
                <ProgressBar
                  label="Critical"
                  percentage={displayData.bySeverity.critical.adherencePct}
                  count={`${displayData.bySeverity.critical.withinSla}/${displayData.bySeverity.critical.total}`}
                  color="bg-purple-500"
                />
                <ProgressBar
                  label="High"
                  percentage={displayData.bySeverity.high.adherencePct}
                  count={`${displayData.bySeverity.high.withinSla}/${displayData.bySeverity.high.total}`}
                  color="bg-red-500"
                />
                <ProgressBar
                  label="Medium"
                  percentage={displayData.bySeverity.medium.adherencePct}
                  count={`${displayData.bySeverity.medium.withinSla}/${displayData.bySeverity.medium.total}`}
                  color="bg-amber-500"
                />
                <ProgressBar
                  label="Low"
                  percentage={displayData.bySeverity.low.adherencePct}
                  count={`${displayData.bySeverity.low.withinSla}/${displayData.bySeverity.low.total}`}
                  color="bg-blue-500"
                />
              </div>
            ) : (
              <p className="text-sm text-ui-text-tertiary">No data available</p>
            )}
          </div>

          {/* Platform Health (using real API data) */}
          {displayData ? (
            <PlatformHealthCard health={displayData.platformHealth} />
          ) : (
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-ui-text-secondary" />
                <h3 className="text-sm font-medium text-ui-text-primary">Platform Health</h3>
              </div>
              <p className="text-sm text-ui-text-tertiary">No data available</p>
            </div>
          )}
        </div>

        {/* Debug Section: Raw Compliance Data from API
            Only shown when BOTH conditions are met:
            1. REACT_APP_DEBUG_COMPLIANCE=true (explicit opt-in)
            2. NODE_ENV !== 'production' (safety check to prevent accidental prod exposure)
        */}
        {process.env.REACT_APP_DEBUG_COMPLIANCE === 'true' && process.env.NODE_ENV !== 'production' && (
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-ui-text-primary">Debug: Compliance Data from API</h3>
              <button
                onClick={fetchComplianceData}
                disabled={isLoadingCompliance}
                className="px-2 py-1 text-xs bg-ui-background-tertiary hover:bg-ui-background-primary text-ui-text-secondary rounded transition-colors disabled:opacity-50"
              >
                {isLoadingCompliance ? 'Loading...' : 'Reload'}
              </button>
            </div>
            {complianceData ? (
              <pre className="text-xs text-ui-text-secondary bg-ui-background-tertiary rounded p-3 overflow-auto max-h-96">
                {JSON.stringify(complianceData, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-ui-text-tertiary">
                {isLoadingCompliance ? 'Loading compliance data...' : 'No compliance data available. Click "Refresh" to compute.'}
              </p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ComplianceOverviewPage;
